/**
 * One-off chart helpers for the metrics page, manually wrapping Recharts to
 * enforce our layout, CSS variable color config, and tooltip presentation.
 */
import * as React from "react";
import { ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";

import { cn } from "@/lib/utils";

export type ChartConfig = Record<
  string,
  {
    label: string;
    color?: string;
  }
>;

type ChartContainerProps = Omit<
  React.ComponentPropsWithoutRef<"div">,
  "children"
> & {
  children: React.ReactElement;
  config: ChartConfig;
};

export const ChartContainer = React.forwardRef<
  HTMLDivElement,
  ChartContainerProps
>(({ children, className, config, style, ...props }, ref) => {
  const colorVars = React.useMemo(() => {
    return Object.entries(config).reduce<React.CSSProperties>(
      (acc, [key, value], index) => {
        const color =
          value.color ?? `hsl(var(--chart-${String(index + 1)}, var(--primary)))`;
        // Store color as a string index signature to satisfy React.CSSProperties requirements
        (acc as Record<string, unknown>)[`--color-${key}`] = color;
        return acc;
      },
      {},
    );
  }, [config]);

  return (
    <div
      ref={ref}
      className={cn("h-[320px] w-full", className)}
      style={{ ...colorVars, ...style }}
      {...props}
    >
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
});
ChartContainer.displayName = "ChartContainer";

export const ChartTooltip = RechartsTooltip as unknown as React.FC<{
  cursor?: {
    stroke?: string;
    strokeWidth?: number;
  };
  content?: React.ReactNode;
  labelFormatter?: (value: string | number) => string;
}>;

interface TooltipPayload {
  color?: string;
  name?: string;
  value?: number | string;
}

interface ChartTooltipContentProps {
  active?: boolean;
  hideLabel?: boolean;
  label?: string | number;
  payload?: TooltipPayload[];
}

export const ChartTooltipContent = ({
  active,
  hideLabel,
  label,
  payload,
}: ChartTooltipContentProps) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="min-w-[160px] rounded-md border border-slate-200 bg-white p-3 shadow-md">
      {!hideLabel && label !== undefined ? (
        <p className="mb-2 text-sm font-semibold text-slate-700">
          {label as string}
        </p>
      ) : null}
      <div className="flex flex-col gap-2 text-xs text-slate-600">
        {payload.map((entry, index) => (
          <div
            key={`${String(entry.name ?? "")}-${String(index)}`}
            className="flex items-center gap-2"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="flex-1">{entry.name}</span>
            <span className="font-semibold text-slate-900">
              {typeof entry.value === "number"
                ? entry.value.toLocaleString()
                : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

